/**
 * Approving or rejecting a verification. Each is one intent with its own
 * Idempotency-Key, sends the `version` the reviewer looked at (ADR-0016), and
 * changes nothing on screen until the server answers. Approving sends the
 * reviewer's reason; rejecting sends a reason code, which the server turns
 * into the pro's notification in their language, and an optional note for
 * the team.
 */
import {
  REJECTION_NOTE_MAX_LENGTH,
  VERIFICATION_REJECTION_REASON_CODES,
  type VerificationRejectionReasonCode,
} from "@wallyo/contracts/console/verifications";
import type { ConsoleClient } from "../../api/client";
import { approveVerification, rejectVerification } from "../../api/console-api";
import { ConsoleApiError } from "../../api/errors";
import { describeError } from "../../auth/error-message";
import { t, type MessageId } from "../../i18n";
import { isOpen, type VerificationDetail } from "./verification-format";

/** The server's bounds (server/http/validation/verifications.ts). */
export const APPROVE_REASON_MIN_LENGTH = 3;
export const APPROVE_REASON_MAX_LENGTH = 500;
export const REJECT_NOTE_MAX_LENGTH = REJECTION_NOTE_MAX_LENGTH;

export type DecisionKind = "approve" | "reject";

/** Why an ID is refused: the server's codes, in the order the dialog offers them. */
export const REJECT_REASON_CODES = VERIFICATION_REJECTION_REASON_CODES;

export const reasonCodeLabel = (code: VerificationRejectionReasonCode): string =>
  t(`verifications.reject.code.${code}` as const);

/** Why approving can't be offered, or null when it can. The server checks all of it again. */
export function approveBlock(detail: VerificationDetail, canApprove: boolean): MessageId | null {
  if (!isOpen(detail.status)) return "verifications.decided";
  if (detail.matchesBannedDocument || detail.bannedDocument !== null || detail.status === "flagged") {
    return "verifications.approve.banned";
  }
  if (!canApprove) return "verifications.approve.locked";
  return null;
}

/**
 * A problem with what the dialog would send, or null when it can be sent: an
 * approval's reason, or a rejection's code (required) and note (optional).
 */
export function reasonProblem(
  kind: DecisionKind,
  reason: string,
  reasonCode: VerificationRejectionReasonCode | null = null,
): string | null {
  const length = reason.trim().length;
  if (kind === "approve") {
    if (length < APPROVE_REASON_MIN_LENGTH)
      return t("verifications.approve.tooShort", { min: APPROVE_REASON_MIN_LENGTH });
    if (length > APPROVE_REASON_MAX_LENGTH)
      return t("verifications.reason.tooLong", { max: APPROVE_REASON_MAX_LENGTH });
    return null;
  }
  if (reasonCode === null) return t("verifications.reject.pickCode");
  if (length > REJECT_NOTE_MAX_LENGTH) return t("verifications.reason.tooLong", { max: REJECT_NOTE_MAX_LENGTH });
  return null;
}

export function sendApprove(client: ConsoleClient, detail: VerificationDetail, reason: string, idempotencyKey: string) {
  return approveVerification(
    client,
    detail.identityVerificationId,
    { reason: reason.trim(), version: detail.version },
    { idempotencyKey },
  );
}

export function sendReject(
  client: ConsoleClient,
  detail: VerificationDetail,
  rejection: { reasonCode: VerificationRejectionReasonCode; note: string },
  idempotencyKey: string,
) {
  const note = rejection.note.trim();
  return rejectVerification(
    client,
    detail.identityVerificationId,
    { reasonCode: rejection.reasonCode, note: note === "" ? null : note, version: detail.version },
    { idempotencyKey },
  );
}

export type FailureContext = DecisionKind | "images";

export type DecisionFailure = {
  message: string;
  reference: string;
  /** `stale_version`: someone decided or changed it meanwhile. Offer a reload. */
  stale: boolean;
};

const REFUSED: Record<FailureContext, MessageId> = {
  approve: "verifications.error.approveRefused",
  reject: "verifications.error.rejectRefused",
  images: "verifications.error.imagesRefused",
};

const INVALID: Record<FailureContext, MessageId> = {
  approve: "verifications.error.approveInvalid",
  reject: "verifications.error.rejectInvalid",
  images: "verifications.error.imagesDeleted",
};

/** The server's reasons (ConsoleErrorReason) this screen has its own words for. */
const BY_REASON: Readonly<Record<string, MessageId>> = {
  banned_document: "verifications.approve.banned",
  images_deleted: "verifications.error.imagesDeleted",
};

/**
 * A failure in this screen's words, chosen by the server's `reason`, else by
 * its code (never the server's English text), with the request ID to quote
 * (ADR-0016 §2.5).
 */
export function describeFailure(error: unknown, context: FailureContext): DecisionFailure {
  const { message, reference } = describeError(error);
  if (!(error instanceof ConsoleApiError)) return { message, reference, stale: false };
  const byReason = error.reason ? BY_REASON[error.reason] : undefined;
  if (byReason) return { message: t(byReason), reference, stale: false };
  // A reason this screen has no words of its own for falls back to its code.
  const kind = error.reason === "stale_version" || error.reason === "feature_not_enabled" ? error.reason : error.code;
  switch (kind) {
    case "stale_version":
      return { message: t("verifications.error.stale"), reference, stale: true };
    case "feature_not_enabled":
      return { message: t(REFUSED[context]), reference, stale: false };
    case "validation_error":
      return {
        message: t(INVALID[context], { max: REJECT_NOTE_MAX_LENGTH }),
        reference,
        stale: false,
      };
    case "network_error":
      return error.outcomeUnknown
        ? { message: t("verifications.error.outcomeUnknown"), reference, stale: false }
        : { message, reference, stale: false };
    default:
      return { message, reference, stale: false };
  }
}
